import { Checkbox } from "@/components/ui/checkbox";
import { useTranslations } from "@/hooks/use-translations";
import { FEATURES, TEAM_FEATURES, type AssignableTeamRole, type Feature, type SiteRole } from "@/lib/access";

export const SITE_ROLES: SiteRole[] = ["user", "admin", "super_admin"];
export const TEAM_ROLES: AssignableTeamRole[] = ["manager", "member", "viewer"];

/** Names of roles and features on the Manager page, in the chosen language. */
export function useManagerLabels() {
	const t = useTranslations("manager");
	return {
		siteRole: (role: SiteRole) => t(`roles.${role}`),
		teamRole: (role: AssignableTeamRole) => t(`teamRoles.${role}`),
		teamRoleShort: (role: AssignableTeamRole | "owner") => t(`teamRolesShort.${role}`),
		feature: (feature: Feature) => t(`features.${feature}`),
		featureShort: (feature: Feature) => t(`featuresShort.${feature}`),
	};
}

export interface AccessValue {
	fullAccess: boolean;
	featureAccess: Feature[];
}

/**
 * Full-Access, or the features one by one, for one space: the personal space
 * (all features) or a team (`team`: no DOOH, it has no team space).
 * Super Admin always has everything.
 */
export function AccessPicker({
	value,
	onChange,
	disabled,
	idPrefix,
	team = false,
	legend,
}: {
	value: AccessValue;
	onChange: (value: AccessValue) => void;
	disabled?: boolean;
	idPrefix: string;
	team?: boolean;
	legend?: string;
}) {
	const t = useTranslations("manager.access");
	const labels = useManagerLabels();
	const features = team ? TEAM_FEATURES : FEATURES;
	const toggle = (feature: Feature, on: boolean) =>
		onChange({
			...value,
			featureAccess: on
				? [...value.featureAccess, feature]
				: value.featureAccess.filter((f) => f !== feature),
		});
	const nothing = !value.fullAccess && value.featureAccess.length === 0;

	return (
		<fieldset className="space-y-2" disabled={disabled}>
			<legend className="mb-1.5 block text-sm font-medium text-text-primary">
				{legend ?? t(team ? "teamLegend" : "personalLegend")}
			</legend>
			<label htmlFor={`${idPrefix}-full`} className="flex items-start gap-2 text-sm text-text-primary">
				<Checkbox
					id={`${idPrefix}-full`}
					checked={value.fullAccess}
					onChange={(e) => onChange({ ...value, fullAccess: e.target.checked })}
					className="mt-0.5"
				/>
				<span>
					{t("full")} <span className="text-text-tertiary">{t(team ? "fullHintTeam" : "fullHint")}</span>
				</span>
			</label>
			{features.map((feature) => (
				<label
					key={feature}
					htmlFor={`${idPrefix}-${feature}`}
					className="flex items-center gap-2 pl-6 text-sm text-text-primary"
				>
					<Checkbox
						id={`${idPrefix}-${feature}`}
						checked={value.fullAccess || value.featureAccess.includes(feature)}
						disabled={disabled || value.fullAccess}
						onChange={(e) => toggle(feature, e.target.checked)}
					/>
					<span className={value.fullAccess ? "text-text-tertiary" : undefined}>{labels.feature(feature)}</span>
				</label>
			))}
			<p className="text-xs text-text-tertiary">{t(team ? "teamNote" : "personalNote")}</p>
			{nothing && <p className="text-xs text-warning">{t(team ? "nothingTeam" : "nothingPersonal")}</p>}
		</fieldset>
	);
}
